import React, { Component } from "react";
import { connect } from "react-redux";
import { string, func, arrayOf, shape, number, date } from "prop-types";
import { FilePond, File, registerPlugin } from "react-filepond";
import FilePondPluginFileValidateSize from "filepond-plugin-file-validate-size";
import FilePondPluginFileValidateType from "filepond-plugin-file-validate-type";
import "filepond/dist/filepond.min.css";

import * as i18n from "util/i18n";
import * as fileMiddleware from "middleware/file";
import { actions as notificationActions } from "reducers/notification";
import { selectors as fileSelectors, actions as fileActions } from "reducers/formFields/multilineFile";
import { selectors as sessionSelectors } from "reducers/session";
import { selectors as accountsSelectors } from "reducers/accounts";
import { selectors as i18nSelectors } from "reducers/i18n";
import { channel } from "middleware/api";
import { bool } from "yup";

// Register the plugins
registerPlugin(FilePondPluginFileValidateSize, FilePondPluginFileValidateType);

class FileUploader extends Component {
    static propTypes = {
        idForm: string,
        idFormField: string,
        idActivity: string,
        description: string,
        onRemoveFile: func,
        onAddFile: func,
        onError: func,
        onFileProcess: func,
        setValue: func,
        lang: string.isRequired,
        idEnvironment: string.isRequired,
        files: arrayOf(),
        idRelatedFile: string,
        accessToken: string.isRequired,
        summary: shape({
            account: string.isRequired,
            amount: number.isRequired,
            currency: string.isRequired,
            description: string,
            typePayment: string,
            file: arrayOf(
                shape({
                    fileId: number,
                    fileName: string,
                    fileSize: number,
                }),
            ),
            dateProcess: date,
        }).isRequired,
        dispatch: func.isRequired,
        acceptedFileTypes: string,
        removeFile: bool,
        fileUploaded: bool,
        accounts: arrayOf(
            shape({
                idProduct: string.isRequired,
            }),
        ).isRequired,
        values: shape({}).isRequired,
    };
    // More props:
    // - https://pqina.nl/filepond/docs/patterns/api/filepond-instance/#properties
    // - https://pqina.nl/filepond/docs/patterns/plugins/file-validate-size/#properties
    // - https://pqina.nl/filepond/docs/patterns/plugins/file-validate-type/#properties
    // - https://pqina.nl/filepond/docs/patterns/plugins/image-preview/#properties

    static defaultProps = {
        idForm: null,
        idFormField: null,
        idActivity: null,
        description: null,
        onRemoveFile: null,
        onAddFile: null,
        setValue: null,
        files: null,
        idRelatedFile: null,
        onError: null,
        onFileProcess: null,
        acceptedFileTypes: "text/plain",
        removeFile: false,
        fileUploaded: false,
    };

    state = {
        files: [],
        removeFile: false,
    };

    shouldComponentUpdate(nextProps) {
        const { files } = this.state;
        const hasCurencyChanged = this.hasCurrencyChanged(this.props, nextProps);

        return nextProps?.files?.length !== files?.length || hasCurencyChanged;
    }

    componentDidUpdate(prevProps) {
        const { dispatch } = this.props;
        const hasCurencyChanged = this.hasCurrencyChanged(prevProps, this.props);
        if (hasCurencyChanged) {
            this.pond.removeFile();
            dispatch(fileActions.onFileRemoved());
        }
    }

    componentWillUnmount() {
        const { setValue } = this.props;
        if (setValue) {
            setValue([]);
        }
    }

    hasCurrencyChanged = (prevProps, currentProps) => {
        const {
            accounts,
            values: { account: prevAccount },
        } = prevProps;

        const {
            values: { account },
        } = currentProps;

        const prevCurrency = accounts.find(({ idProduct }) => idProduct === prevAccount).currency;
        const { currency } = accounts.find(({ idProduct }) => idProduct === account);

        return prevCurrency !== currency;
    };

    onProcessFile = (error, fileItem) => {
        const { onAddFile } = this.props;
        if (!error && fileItem) {
            const { files } = this.props;
            const file = {
                fileId: parseInt(fileItem.serverId, 10),
                fileName: fileItem.filename,
                fileSize: fileItem.fileSize,
                fileType: fileItem.fileType,
            };

            this.setState({ files: [...files, file] }, () => {
                if (onAddFile) {
                    onAddFile(file);
                }
            });
            document.getElementsByClassName("filepond--wrapper")[0].className =
                "filepond--wrapper border-none pb-15 pr-1";
        }
    };

    onRemoveFile = ({ serverId }) => {
        const { onRemoveFile, idRelatedFile } = this.props;
        const { files } = this.state;

        this.setState({ removeFile: true });

        this.setState({ files: files.filter((file) => file.fileId !== serverId) }, () => {
            if (onRemoveFile) {
                onRemoveFile(parseInt(serverId, 10));
            }
        });
        document.getElementsByClassName("filepond--wrapper")[0].className = "filepond--wrapper";
        fileMiddleware.deleteFile(parseInt(serverId, 10));
        if (idRelatedFile) {
            fileMiddleware.deleteFile(parseInt(idRelatedFile, 10));
        }
        return true;
    };

    onUpdateFiles = (fileItems) => {
        const { dispatch, acceptedFileTypes, fileUploaded } = this.props;
        const { removeFile } = this.state;
        const invalidFileErrorMessage = i18n.get("forms.inputFile.massivePayments.invalid.file.type");
        const error = fileItems.some((fileItem) => {
            if (fileItem.file.size === 0) {
                return true;
            }

            if (fileItem.fileType !== acceptedFileTypes) {
                return true;
            }

            if (!fileUploaded && !removeFile) {
                document
                    .getElementsByClassName("filepond--item")[0]
                    .setAttribute("data-filepond-item-state", "processing-complete");
                document.getElementsByClassName("filepond--file-info")[0].style.transform =
                    "translate3d(0px, 0px, 0px)";
                document.getElementsByClassName("filepond--file-status")[0].style.opacity = 1;
                document.getElementsByClassName("filepond--file-status")[0].style.visibility = "visible";
                document.getElementsByClassName("filepond--file-status")[0].style.transform =
                    "translate3d(0px, 0px, 0px)";
                document.getElementsByClassName("filepond--file-status-main")[0].innerHTML = i18n.get(
                    "file.upload.input.labelFileProcessingComplete",
                );
                document.getElementsByClassName("filepond--file-status-sub")[0].innerHTML = i18n.get(
                    "file.upload.input.labelTapToUndo",
                );
                document
                    .getElementsByClassName("filepond--file-action-button filepond--action-remove-item")[0]
                    .setAttribute("data-align", "right");
                document.getElementsByClassName("filepond--wrapper")[0].className =
                    "filepond--wrapper border-none pb-15 pr-1";
            }
            return false;
        });
        if (error) {
            dispatch(notificationActions.showNotification(invalidFileErrorMessage, "error", ["form"]));
        }
    };

    getLabels = () => ({
        labelIdle: i18n.get("file.upload.input.labelIdle"),
        labelFileWaitingForSize: i18n.get("file.upload.input.labelFileWaitingForSize"),
        labelFileSizeNotAvailable: i18n.get("file.upload.input.labelFileSizeNotAvailable"),
        labelFileLoading: i18n.get("file.upload.input.labelFileLoading"),
        labelFileLoadError: i18n.get("file.upload.input.labelFileLoadError"),
        labelFileProcessing: i18n.get("file.upload.input.labelFileProcessing"),
        labelFileProcessingComplete: i18n.get("file.upload.input.labelFileProcessingComplete"),
        labelFileProcessingAborted: i18n.get("file.upload.input.labelFileProcessingAborted"),
        labelFileProcessingError: i18n.get("file.upload.input.labelFileProcessingError"),
        labelTapToCancel: i18n.get("file.upload.input.labelTapToCancel"),
        labelTapToRetry: i18n.get("file.upload.input.labelTapToRetry"),
        labelTapToUndo: i18n.get("file.upload.input.labelTapToUndo"),
        labelButtonRemoveItem: i18n.get("file.upload.input.labelButtonRemoveItem"),
        labelButtonAbortItemLoad: i18n.get("file.upload.input.labelButtonAbortItemLoad"),
        labelButtonRetryItemLoad: i18n.get("file.upload.input.labelButtonRetryItemLoad"),
        labelButtonAbortItemProcessing: i18n.get("file.upload.input.labelButtonAbortItemProcessing"),
        labelButtonUndoItemProcessing: i18n.get("file.upload.input.labelButtonUndoItemProcessing"),
        labelButtonRetryItemProcessing: i18n.get("file.upload.input.labelButtonRetryItemProcessing"),
        labelButtonProcessItem: i18n.get("file.upload.input.labelButtonProcessItem"),
        labelMaxFileSizeExceeded: i18n.get("file.upload.input.labelMaxFileSizeExceeded"),
        labelMaxFileSize: i18n.get("file.upload.input.labelMaxFileSize"),
        labelMinFileSize: i18n.get("file.upload.input.labelTapToUndo"),
        labelMinFileSizeExceeded: i18n.get("file.upload.input.labelFileTypeNotAllowed"),
        labelTotalFileSizeExceeded: i18n.get("file.upload.input.labelTotalFileSizeExceeded"),
        labelMaxTotalFileSize: i18n.get("file.upload.input.labelMaxTotalFileSize"),
        labelFileTypeNotAllowed: i18n.get("file.upload.input.labelFileTypeNotAllowed"),
        fileValidateTypeLabelExpectedTypes: i18n.get("file.upload.input.fileValidateTypeLabelExpectedTypes"),
    });

    // TODO: Pending migrate this logic to use our middleware (Axios)
    getServerOptions = () => ({
        load: null,
        fetch: null,
        restore: null,

        process: (fieldName, file, metadata, load, error, progress, abort) => {
            const {
                idForm,
                idFormField,
                idActivity,
                description,
                accessToken,
                lang,
                idEnvironment,
                accounts,
                values: { account },
            } = this.props;
            // fieldName is the name of the input field
            // file is the actual file object to send
            const formData = new FormData();

            if (idForm) {
                formData.append("idForm", idForm);
            }
            if (idFormField) {
                formData.append("idFormField", idFormField);
            }
            if (idActivity) {
                formData.append("idActivity", idActivity);
            }
            if (account) {
                const completeDebitAccount = accounts.find(({ idProduct }) => idProduct === account);
                // RootResource v13 no propaga @FormDataParam("currency"); viaja con idProduct en description.
                if (completeDebitAccount) {
                    formData.append(
                        "description",
                        `${completeDebitAccount.idProduct}|${completeDebitAccount.currency}`,
                    );
                    formData.append("currency", completeDebitAccount.currency);
                } else if (description) {
                    formData.append("description", description);
                }
            } else if (description) {
                formData.append("description", description);
            }
            formData.append("_file", file, file.name);
            formData.append("channel", channel());
            formData.append("idEnvironment", idEnvironment);
            formData.append("lang", lang);

            const request = new XMLHttpRequest();
            request.open("POST", `${window.API_URL}/files.upload`);
            request.setRequestHeader("Authorization", `bearer ${accessToken}`);
            // OauthSessionHandlerOIDC resuelve idEnvironment/lang desde headers HTTP, no desde FormData.
            if (idEnvironment != null && idEnvironment !== "") {
                request.setRequestHeader("idEnvironment", String(idEnvironment));
            }
            if (lang) {
                request.setRequestHeader("lang", lang);
            }
            // Should call the progress method to update the progress to 100% before calling load
            // Setting computable to false switches the loading indicator to infinite mode
            request.upload.onprogress = (e) => {
                progress(e.lengthComputable, e.loaded, e.total);
            };

            // Should call the load method when done and pass the returned server file id
            // this server file id is then used later on when reverting or restoring a file
            // so your server knows which file to return without exposing that info to the client
            request.onload = () => {
                const { onError } = this.props;
                const errorMessage = i18n.get("forms.notification.uploadFile.fail");
                if (request.status >= 200 && request.status < 300) {
                    const response = JSON.parse(request.responseText);
                    if (response.code.endsWith("I")) {
                        const { onFileProcess } = this.props;
                        const { idFile, validLines, invalidLines, ...data } = response.data;
                        const total = validLines + invalidLines;
                        const uploadFileSuccessMessage = i18n.get("forms.notification.uploadFile.success");
                        const validLinesMessage = i18n.get("forms.notification.uploadFile.success.validLines");
                        const message = `${uploadFileSuccessMessage}  ${validLines} de  ${total} ${validLinesMessage}`;

                        load(response.data.idFile);

                        if (onFileProcess) {
                            onFileProcess({ idFile, message, validLines, invalidLines, ...data });
                        }
                    } else {
                        if (onError) {
                            onError({ errorMessage, ...response.data });
                        }
                        error("oh");
                    }
                } else {
                    // Can call the error method if something is wrong, should exit after
                    if (onError) {
                        onError({ errorMessage });
                    }
                    error("oh no");
                }
            };

            request.send(formData);

            // Should expose an abort method so the request can be cancelled
            return {
                abort: () => {
                    // This function is entered if the user has tapped the cancel button
                    request.abort();

                    // Let FilePond know the request has been cancelled
                    abort();
                },
            };
        },
        revert: (uniqueFileId, load) => {
            // Should remove the earlier created temp file here
            if (uniqueFileId) {
                this.onRemoveFile({ serverId: uniqueFileId });
            }
            load();
        },
    });

    render() {
        const {
            idForm,
            idFormField,
            idActivity,
            description,
            accessToken,
            files,
            acceptedFileTypes,
            ...rest
        } = this.props;
        const labels = this.getLabels();
        const serverOptions = this.getServerOptions();
        return (
            <FilePond
                ref={(ref) => {
                    this.pond = ref;
                }}
                server={serverOptions}
                {...labels}
                isDesktop
                {...rest}
                acceptedFileTypes={acceptedFileTypes}
                onprocessfile={this.onProcessFile}
                beforeRemoveFile={this.onRemoveFile}
                onupdatefiles={this.onUpdateFiles}>
                {/* Update current files  */}
                {files &&
                    files.map((fileItem) => (
                        <File
                            key={fileItem.fileId}
                            src={fileItem.fileId}
                            origin="local"
                            name={fileItem.fileName}
                            size={fileItem.fileSize}
                            type={fileItem.fileType}
                        />
                    ))}
            </FilePond>
        );
    }
}

const mapStateToProps = (state) => ({
    accessToken: sessionSelectors.getAccessToken(state),
    idRelatedFile: fileSelectors.getIdRelatedFile(state),
    lang: i18nSelectors.getLang(state),
    idEnvironment: sessionSelectors.getActiveEnvironment(state).id,
    accounts: accountsSelectors.getAccounts(state),
});

export default connect(mapStateToProps)(FileUploader);
